import { useEffect, useRef, useState } from "react";
import "./TemplateFormModal.css";
import "../components/MessagingButtons.css";
import "../components/MessageBodyEditor.css";
import { MessageTemplate } from "../../../models/MessageModels";
import {
  MessageChannel,
  MessageType,
  MESSAGE_CHANNEL_LABELS,
  MESSAGE_PLACEHOLDERS,
  MESSAGE_TYPE_LABELS,
  renderBody,
  withBanglaContactLine,
} from "../../../models/MessageModels";

export type TemplateModalMode = "create" | "edit";

export interface TemplateFormValues {
  name: string;
  channel: number;
  messageType: number;
  body: string;
  isActive: boolean;
}

interface TemplateFormModalProps {
  isOpen: boolean;
  mode: TemplateModalMode;
  template: MessageTemplate | null;
  submitting: boolean;
  onClose: () => void;
  onSubmit: (values: TemplateFormValues) => Promise<void>;
}

const emptyForm: TemplateFormValues = {
  name: "",
  channel: MessageChannel.WhatsApp,
  messageType: MessageType.General,
  body: "",
  isActive: true,
};

const fromTemplate = (template: MessageTemplate): TemplateFormValues => ({
  name: template.name,
  channel: template.channel,
  messageType: template.messageType,
  body: template.body,
  isActive: template.isActive,
});

const TemplateFormModal = ({
  isOpen,
  mode,
  template,
  submitting,
  onClose,
  onSubmit,
}: TemplateFormModalProps) => {
  const [form, setForm] = useState<TemplateFormValues>(emptyForm);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setForm(mode === "edit" && template ? fromTemplate(template) : emptyForm);
  }, [isOpen, mode, template]);

  if (!isOpen) return null;

  const insertPlaceholder = (token: string) => {
    const ta = textareaRef.current;
    const start = ta?.selectionStart ?? form.body.length;
    const end = ta?.selectionEnd ?? form.body.length;
    const next = form.body.slice(0, start) + token + form.body.slice(end);
    setForm((prev) => ({ ...prev, body: next }));
    requestAnimationFrame(() => {
      ta?.focus();
      const pos = start + token.length;
      ta?.setSelectionRange(pos, pos);
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({ ...form, body: withBanglaContactLine(form.body) });
  };

  const canSubmit =
    form.name.trim().length > 0 && form.body.trim().length > 0;

  return (
    <div className="msg-template-modal-overlay" role="dialog" aria-modal="true">
      <div className="msg-template-modal" onClick={(e) => e.stopPropagation()}>
        <div className="msg-template-modal__header">
          <h3>{mode === "create" ? "New Template" : "Edit Template"}</h3>
          <button
            className="msg-template-modal__close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <form className="msg-template-modal__body" onSubmit={handleSubmit}>
          <label className="msg-editor__field">
            <span className="msg-editor__label">Name *</span>
            <input
              type="text"
              value={form.name}
              placeholder="e.g. Follow-up 20% off"
              onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
            />
          </label>

          <div className="msg-template-modal__row">
            <label className="msg-editor__field">
              <span className="msg-editor__label">Channel</span>
              <select
                value={String(form.channel)}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, channel: Number(e.target.value) }))
                }
              >
                {Object.entries(MESSAGE_CHANNEL_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>

            <label className="msg-editor__field">
              <span className="msg-editor__label">Message type</span>
              <select
                value={String(form.messageType)}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    messageType: Number(e.target.value),
                  }))
                }
              >
                {Object.entries(MESSAGE_TYPE_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="msg-editor__field">
            <span className="msg-editor__label">Body *</span>
            <textarea
              ref={textareaRef}
              rows={6}
              value={form.body}
              placeholder="Hello {ClientName}, this is {OrgName}…"
              onChange={(e) =>
                setForm((prev) => ({ ...prev, body: e.target.value }))
              }
            />
          </div>

          <div className="msg-editor__field">
            <span className="msg-editor__label">Insert placeholder</span>
            <div className="msg-editor__chips">
              {MESSAGE_PLACEHOLDERS.map(({ token, label }) => (
                <button
                  key={token}
                  type="button"
                  className="msg-editor__chip"
                  title={`Insert ${token} (${label})`}
                  onClick={() => insertPlaceholder(token)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div className="msg-editor__field">
            <span className="msg-editor__label">Preview</span>
            <div className="msg-editor__preview">
              {form.body ? (
                renderBody(form.body, {
                  ClientName: "Sample Client",
                  OrgName: "Sample Clinic",
                  Phone: "01XXXXXXXXX",
                })
              ) : (
                <span className="msg-editor__muted">
                  Preview appears once you type a body.
                </span>
              )}
            </div>
          </div>

          <label className="msg-template-modal__check">
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, isActive: e.target.checked }))
              }
            />
            Active
          </label>

          <div className="msg-template-modal__actions">
            <button
              type="button"
              className="msg-btn msg-btn--secondary"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="msg-btn msg-btn--primary"
              disabled={submitting || !canSubmit}
            >
              {submitting
                ? "Saving…"
                : mode === "create"
                  ? "Create Template"
                  : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TemplateFormModal;